/**
 * The one-time welcome owed to a freshly created workspace.
 *
 * A module variable, because that is exactly the lifetime the handoff has: the
 * Done screen navigates into the workspace inside the same document, so the
 * shell mounts while this module is still loaded, and a reload — which is not a
 * fresh creation — finds nothing and plays nothing. The previous build kept it
 * in `localStorage`, where a welcome could survive for days waiting for a
 * workspace the user had long since opened somewhere else.
 */
let pending: number | null = null

export const markWorkspaceWelcome = (workspaceId: number): void => {
  pending = workspaceId
}

/**
 * Whether a welcome is owed. Deliberately a pure read: the caller decides it
 * during render, where clearing as a side effect would be consumed twice under
 * StrictMode's double invocation and the welcome would never show in dev.
 */
export const peekWorkspaceWelcome = (workspaceId: number): boolean =>
  pending === workspaceId

/**
 * Spends the marker. Called once the welcome is actually on screen, which is
 * what stops a remount mid-animation from replaying it. Idempotent.
 */
export const clearWorkspaceWelcome = (workspaceId: number): void => {
  if (pending === workspaceId) pending = null
}

/**
 * The checklist's one-time intro, owed to the same freshly created workspace:
 * the setup tasks open by themselves once, so the first thing after the welcome
 * is the list of what is left to do rather than an empty overview.
 *
 * A marker of its own rather than a second read of the welcome's. The welcome
 * spends its marker the moment its animation starts and this one is claimed
 * after that animation is over, so a single value could not serve both.
 */
let pendingChecklistIntro: number | null = null

export const markChecklistIntro = (workspaceId: number): void => {
  pendingChecklistIntro = workspaceId
}

/** Pure read, for the same reason as {@link peekWorkspaceWelcome}. */
export const peekChecklistIntro = (workspaceId: number): boolean =>
  pendingChecklistIntro === workspaceId

/** Spends the marker, once the panel is actually opening. Idempotent. */
export const clearChecklistIntro = (workspaceId: number): void => {
  if (pendingChecklistIntro === workspaceId) pendingChecklistIntro = null
}
